import { get } from "lodash";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Form } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import PromptModal from "src/components/ConfirmationModal/PromptModalV2";
import CheckboxField from "src/components/Form/CheckboxFieldV2/CheckboxFieldV2";
import TextField from "src/components/Form/TextFieldV2/TextFieldV2";
import DisTable from "src/components/TableV2/Table";
import useAutosave from "src/hooks/useAutosave";
import { IApplicationAlert } from "src/store/models/application.model";
import { IRootState } from "src/store/reducers";
import { fetchAppAlertsList, saveApplicationAlert } from "src/store/reducers/applicationAlerts";
import * as S from "../../../../constants/StringConstants";
import { PageContainerButtons } from "../../PageContainer";
import { TabProps } from "../../pages";

const MAX_ALERT_TEXT_LENGTH = 200;
const columns = [
  {
    key: "active",
    title: S.APP_ALERTS_COL_ACTIVE,
    style: { minWidth: "5rem" },
    render: item => (item.active ? S.APP_ALERTS_STATUS_ACTIVE : S.APP_ALERTS_STATUS_INACTIVE),
  },
  { key: "createdBy", title: S.APP_ALERTS_COL_ADDED_BY, style: { minWidth: "5rem" } },
  {
    key: "createdOn",
    title: S.APP_ALERTS_COL_ADDED,
    style: { minWidth: "5rem" },
    render: item => moment(item.createdOn).format("MM/DD/YYYY [at] h A"),
  },
  {
    key: "alertText",
    title: S.APP_ALERTS_COL_TEXT,
    style: { minWidth: "5rem", maxWidth: "20rem" },
    render: item =>
      item.alertText.length > MAX_ALERT_TEXT_LENGTH
        ? `${item.alertText.substring(0, MAX_ALERT_TEXT_LENGTH)}...`
        : item.alertText,
  },
];

export const AppAlerts = (props: TabProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<IApplicationAlert | null>(null);
  const { id: applicationId } = useParams<{ id: string }>();
  const [refreshKey, setRefreshKey] = useState(0);
  const dispatch = useDispatch();

  const userId = useSelector((state: IRootState) => get(state, "auth.user.userID", null));

  const AutoSave = useAutosave(props, () => {});

  useEffect(() => {
    if (applicationId) {
      dispatch(fetchAppAlertsList(applicationId));
    }
  }, [dispatch, applicationId]);

  const handleRowClick = (alert: IApplicationAlert) => {
    setSelectedAlert(alert);
    setIsModalOpen(true);
  };

  const handleSaveAlert = async (values: { alertStatus: boolean; text: string }) => {
    let payload: any = {
      applicationAlertHeaderId: selectedAlert?.applicationAlertHeaderId ?? 0,
      applicationid: Number(applicationId),
      active: values.alertStatus,
      alertText: values.text,
      userId: userId,
    };

    try {
      await dispatch(saveApplicationAlert(applicationId, payload));
      setIsModalOpen(false);
      setRefreshKey(prev => prev + 1); // updates table
    } catch (error) {}
  };

  return (
    <>
      <DisTable<IApplicationAlert>
        key={refreshKey}
        columns={columns}
        initialState={{}}
        onRowClick={handleRowClick}
        onUpdate={async () => {
          const result: any = await dispatch(fetchAppAlertsList(applicationId));
          return {
            total: get(result, "value.data").length as number,
            data: get(result, "value.data") as IApplicationAlert[],
          };
        }}
      />

      <PageContainerButtons>
        <button
          className="button-v2 button-v2__secondary"
          onClick={() => {
            setSelectedAlert(null);
            setIsModalOpen(true);
          }}>
          {S.APP_ALERTS_ADD_ALERT_TITLE}
        </button>
        <AutoSave />
      </PageContainerButtons>

      <Form
        initialValues={{
          alertStatus: selectedAlert?.active,
          text: selectedAlert?.alertText,
        }}
        onSubmit={handleSaveAlert}>
        {({ handleSubmit, form }) => (
          <PromptModal
            open={isModalOpen}
            title={selectedAlert ? S.APP_ALERTS_EDIT_ALERT_TITLE : S.APP_ALERTS_ADD_ALERT_TITLE}
            confirmText={selectedAlert ? S.APP_ALERTS_MODAL_SAVE_BUTTON : S.APP_ALERTS_MODAL_ADD_BUTTON}
            rejectText={S.APP_ALERTS_MODAL_CANCEL_BUTTON}
            onReject={() => setIsModalOpen(false)}
            onConfirm={handleSubmit}>
            <form onSubmit={handleSubmit}>
              <div className="additional-insureds-modal__card_container">
                <div>
                  <CheckboxField name="alertStatus" label="Active" />
                </div>
                <TextField name="text" multiline />
              </div>
            </form>
          </PromptModal>
        )}
      </Form>
    </>
  );
};
